import type {
  ComponentProps,
  ComponentType,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  UIEvent
} from 'react';
import {
  cloneElement,
  forwardRef,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';
import { useDelayedUnmount } from '@elfsight/lib-utils';
import { invariant } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useRect } from 'react-use-rect';
import { Backdrop } from '../backdrop';
import { PopupCloseControl } from '../popup-close-control';
import { Portal } from '../portal';
import { useScrollLock } from '../utils';
import { zIndexLayer } from '../zIndexLayer';

export const POPUP_ANIMATION_DURATION_MS = 300;
const CLOSING_CLASS = 'popup-wrapper-closing';

export type PopupProps = PropsWithChildren<{
  show: boolean;
  width?: number | string;
  className?: string;
  fullscreen?: boolean;
  fit?: boolean;
  withCloseControl?: boolean;
  closeControlComponent?: ComponentType<
    ComponentProps<typeof PopupCloseControl>
  >;
  closeControlPosition?: CloseControlPosition;
  closeControlColor?: string;
  withBackdrop?: boolean;
  backdropComponent?: ReactElement;
  backdropColor?: string;
  backdropStyle?: CSSProperties;
  margin?: number | [number, number];
  disableScroll?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  portalContainerElement?: Element;
  fixed?: boolean;
  zIndex?: number;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  withoutTransition?: boolean;
  disablePointerEvents?: boolean;
  disableFocusOnOpen?: boolean;
  ariaLabel?: string;
  shadow?: boolean;
  onCloseRequest?: (event?: MouseEvent | KeyboardEvent) => void;
  onScroll?: (event: UIEvent) => void;
}>;

export const Popup = forwardRef<HTMLDivElement, PopupProps>(function Popup(
  {
    children,
    show,
    width,
    className,
    fullscreen = false,
    fit = false,
    withCloseControl = false,
    closeControlPosition = 'inside',
    closeControlComponent: FinalCloseControl = PopupCloseControl,
    closeControlColor,
    withBackdrop = true,
    backdropComponent,
    backdropColor,
    backdropStyle,
    margin,
    portalContainerElement,
    disableScroll = false,
    closeOnOutsideClick = false,
    closeOnEscape = false,
    fixed = false,
    zIndex = zIndexLayer.modal,
    style,
    containerStyle,
    withoutTransition = false,
    disablePointerEvents = false,
    disableFocusOnOpen,
    ariaLabel,
    shadow,
    onCloseRequest,
    onScroll
  },
  forwardingRef
) {
  let marginHorizontal = 0;
  let marginVertical = 0;

  if (margin) {
    if (Array.isArray(margin)) {
      invariant(
        margin.length === 2,
        'Expected the margin to be a [number, number] ' +
          '(horizontal margin, vertical margin) tuple.'
      );

      [marginHorizontal, marginVertical] = margin;
    } else {
      marginHorizontal = margin;
      marginVertical = margin;
    }
  }

  const requestClose = (event?: MouseEvent | KeyboardEvent) => {
    if (show && onCloseRequest) {
      onCloseRequest(event);
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (closeOnOutsideClick && innerRef.current === event.target) {
      requestClose(event);
    }
  };

  const animationDuration = withoutTransition ? 0 : POPUP_ANIMATION_DURATION_MS;
  const [isMounted, isClosing] = useDelayedUnmount(show, animationDuration);
  const isVisible = show && !isClosing;

  const [contentHeight, setContentHeight] = useState(0);
  const [contentRectRef] = useRect((rect) => setContentHeight(rect.height), {
    resize: true
  });

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerRectRef] = useRect((rect) => setContainerHeight(rect.height));

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const scrollableWrapperRef = useRef<HTMLDivElement | null>(null);

  // center the content if it's not fullscreen mode
  // and we can fit the content into the container
  const contentCentered =
    !fullscreen && contentHeight < containerHeight - marginVertical * 2;

  const limitedHeight = fullscreen || fit;

  const withInsideCloseControl =
    withCloseControl && closeControlPosition === 'inside';

  const withOutsideCloseControl =
    withCloseControl && closeControlPosition === 'outside';

  const handleWrapperKeydown = (event: KeyboardEvent) => {
    if (closeOnEscape && event.key === 'Escape') {
      requestClose(event);
    }
  };

  useLayoutEffect(() => {
    if (wrapperRef.current && show && !disableFocusOnOpen) {
      wrapperRef.current.focus({ preventScroll: true });
    }
  }, [show, disableFocusOnOpen]);

  const lockScroll: boolean = disableScroll && (show || !isClosing);

  useScrollLock(wrapperRef, lockScroll);

  const wrapperMergedRef = useMergedRef(forwardingRef, wrapperRef);

  const FinalBackdrop = backdropComponent ? (
    cloneElement(backdropComponent, {
      ...backdropComponent.props,
      style: {
        zIndex,
        ...backdropComponent.props.style
      }
    })
  ) : (
    <Backdrop
      background={backdropColor}
      fixed={fixed}
      zIndex={zIndex}
      open={isVisible}
      withoutTransition={withoutTransition}
      style={backdropStyle}
    />
  );

  return (
    isMounted && (
      <Portal containerElement={portalContainerElement} shadow={shadow}>
        <PopupWrapper
          ref={wrapperMergedRef}
          tabIndex={-1}
          role="dialog"
          aria-label={ariaLabel}
          aria-modal="true"
          onKeyDown={handleWrapperKeydown}
        >
          {withBackdrop && FinalBackdrop}

          <PopupContainer
            _fixed={fixed}
            _zIndex={zIndex}
            _disablePointerEvents={disablePointerEvents}
            _animationDuration={animationDuration}
            ref={containerRectRef}
            className={isVisible ? '' : CLOSING_CLASS}
            style={containerStyle}
          >
            {withOutsideCloseControl && (
              <PopupCloseOutside>
                <FinalCloseControl
                  color={closeControlColor}
                  onClick={requestClose}
                />
              </PopupCloseOutside>
            )}

            <PopupScrollableWrapper
              ref={scrollableWrapperRef}
              onScroll={onScroll}
            >
              <PopupInner
                _contentCentered={contentCentered}
                _limitedHeight={limitedHeight}
                _padding={`${marginVertical}px ${marginHorizontal}px`}
                ref={innerRef}
                onClick={handleOutsideClick}
              >
                <PopupContent
                  _fullscreen={fullscreen}
                  _limitedHeight={limitedHeight}
                  _isVisible={isVisible}
                  _animationDuration={animationDuration}
                  _width={width}
                  ref={contentRectRef}
                  style={style}
                  className={className}
                >
                  {withInsideCloseControl && (
                    <PopupCloseInside>
                      <PopupCloseInsideInner>
                        <FinalCloseControl
                          color={closeControlColor}
                          onClick={requestClose}
                        />
                      </PopupCloseInsideInner>
                    </PopupCloseInside>
                  )}

                  {children}
                </PopupContent>
              </PopupInner>
            </PopupScrollableWrapper>
          </PopupContainer>
        </PopupWrapper>
      </Portal>
    )
  );
});

type CloseControlPosition = 'inside' | 'outside';

const PopupWrapper = styled.div`
  &&& {
    outline: 0;
  }
`;

export const PopupContainer = styled.div<{
  _fixed: boolean;
  _zIndex: number;
  _disablePointerEvents: boolean;
  _animationDuration: number;
}>`
  ${({ _fixed, _zIndex, _disablePointerEvents, _animationDuration }) => css`
    position: ${_fixed ? 'fixed' : 'absolute'};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${_zIndex};
    ${_disablePointerEvents &&
    css`
      pointer-events: none;
    `};
    transition: opacity ${_animationDuration}ms ease-out;

    &.${CLOSING_CLASS} {
      opacity: 0;
    }

    @starting-style {
      & {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      & {
        transition: none;
      }
    }
  `}
`;

export const PopupScrollableWrapper = styled.div`
  overflow: hidden auto;
  height: 100%;
`;

export const PopupInner = styled.div<{
  _contentCentered: boolean;
  _limitedHeight: boolean;
  _padding: string;
}>`
  ${({ _contentCentered, _limitedHeight, _padding }) => css`
    display: flex;
    min-height: 100%;
    flex-direction: column;
    justify-content: ${_contentCentered ? 'center' : 'flex-start'};
    align-items: center;
    box-sizing: border-box;
    padding: ${_padding};

    ${_limitedHeight &&
    css`
      height: 100%;
    `}
  `}
`;

export const PopupContent = styled.div<{
  _fullscreen: boolean;
  _limitedHeight: boolean;
  _isVisible: boolean;
  _animationDuration: number;
  _width: number | string | undefined;
}>`
  ${({ _fullscreen, _limitedHeight, _width }) => css`
    flex: ${_fullscreen ? 1 : '0 1'};
    position: relative;
    z-index: 1;
    pointer-events: all;
    max-width: 100%;
    ${_width &&
    css`
      width: ${_fullscreen
        ? '100%'
        : typeof _width === 'number'
          ? `${_width}px`
          : _width};
    `};

    ${_fullscreen &&
    css`
      height: 100%;
    `};

    ${_limitedHeight &&
    css`
      max-height: 100%;
    `}
  `}
`;

export const PopupCloseInside = styled.div`
  position: sticky;
  z-index: 10;
  top: 0;
  height: 0;
`;

export const PopupCloseInsideInner = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

export const PopupCloseOutside = styled.div`
  position: absolute;
  z-index: 10;
  top: 16px;
  right: 16px;
`;
